<template>
  <div class="optical-experiment">
    <div id="controls">
      <div class="control-group">
        <label for="focalLength">Фокусное расстояние <strong>F</strong>:</label>
        <input
          type="range"
          id="focalLength"
          min="40"
          max="220"
          v-model.number="lens.focalLength"
          step="5"
        />
        <span class="value">{{ lens.focalLength }}</span> <span class="unit">px</span>
      </div>

      <div class="control-group">
        <label for="objectHeight">Высота предмета <strong>h</strong>:</label>
        <input
          type="range"
          id="objectHeight"
          min="30"
          max="120"
          v-model.number="obj.height"
          step="5"
        />
        <span class="value">{{ obj.height }}</span> <span class="unit">px</span>
      </div>

      <div class="control-group">
        <label for="lensHeight">Размер линзы:</label>
        <input
          type="range"
          id="lensHeight"
          min="80"
          max="220"
          v-model.number="lens.height"
          step="10"
        />
        <span class="value">{{ lens.height }}</span> <span class="unit">px</span>
      </div>

      <button class="btn-reset" @click="reset">Сбросить</button>
    </div>

    <div class="info-panel">
      <div class="info-item">
        <span class="info-label">d (до предмета):</span>
        <span class="info-value">{{ d.toFixed(1) }} px</span>
      </div>
      <div class="info-item">
        <span class="info-label">d′ (до изображения):</span>
        <span class="info-value">{{ dPrimeDisplay }}</span>
      </div>
      <div class="info-item">
        <span class="info-label">Увеличение Γ:</span>
        <span class="info-value">{{ gammaDisplay }}</span>
      </div>
      <div class="info-item">
        <span class="info-label">Характер:</span>
        <span class="info-value" :class="imageNatureClass">{{ imageNatureText }}</span>
      </div>
    </div>

    <canvas
      id="opticsCanvas"
      ref="canvas"
      @mousedown="onMouseDown"
      @mousemove="onMouseMove"
      @mouseup="onMouseUp"
      @mouseleave="onMouseUp"
    ></canvas>

    <div class="hint">
      Перетаскивайте <strong>предмет</strong> (стрелку слева) и <strong>линзу</strong> (центр).
      Наблюдайте за характерными лучами и положением изображения.
    </div>
  </div>
</template>

<script>
export default {
  name: "OpticalExperiment",
  data() {
    return {
      canvasWidth: 1000,
      canvasHeight: 500,
      opticalAxisY: 250,
      lens: {
        x: 500,
        focalLength: 100,
        width: 16,
        height: 140,
        isDragging: false,
      },
      obj: {
        x: 250,
        height: 80,
        isDragging: false,
      },
      dragTarget: null, // 'lens' | 'object' | null
      image: {
        x: 0,
        y: 0,
        height: 0,
        real: true,
        upright: false,
        magnification: 0,
        exists: false,
      },
      animationFrameId: null,
    };
  },
  computed: {
    d() {
      return Math.max(1, this.lens.x - this.obj.x);
    },
    dPrime() {
      const f = this.lens.focalLength;
      const d = this.d;
      if (Math.abs(d - f) < 2) return Infinity;
      return (f * d) / (d - f);
    },
    dPrimeDisplay() {
      if (!isFinite(this.dPrime)) return "∞";
      return this.dPrime.toFixed(1) + " px";
    },
    gamma() {
      if (!isFinite(this.dPrime)) return Infinity;
      const g = -this.dPrime / this.d;
      return g;
    },
    gammaDisplay() {
      if (!isFinite(this.gamma)) return "∞";
      return Math.abs(this.gamma).toFixed(2);
    },
    imageNatureText() {
      if (this.image.outOfView) return "вне поля зрения";
      if (!this.image.exists) return "отсутствует (d ≈ F)";
      const parts = [];
      parts.push(this.image.real ? "действительное" : "мнимое");
      parts.push(this.image.upright ? "прямое" : "перевёрнутое");
      if (this.image.magnification > 1.05) parts.push("увеличенное");
      else if (this.image.magnification < 0.95) parts.push("уменьшенное");
      else parts.push("равное");
      return parts.join(", ");
    },
    imageNatureClass() {
      return {
        "nature-real": this.image.real,
        "nature-virtual": !this.image.real,
      };
    },
  },
  watch: {
    "lens.focalLength": "computeImage",
    "obj.height": "computeImage",
    "lens.x": "computeImage",
    "obj.x": "computeImage",
  },
  mounted() {
    const canvas = this.$refs.canvas;
    canvas.width = this.canvasWidth;
    canvas.height = this.canvasHeight;
    this.ctx = canvas.getContext("2d");
    this.computeImage();
    this.animate();
  },
  beforeUnmount() {
    cancelAnimationFrame(this.animationFrameId);
  },
  methods: {
    reset() {
      this.lens.x = 500;
      this.lens.focalLength = 100;
      this.obj.x = 250;
      this.obj.height = 80;
      this.lens.height = 140;
      this.computeImage();
    },
    isWithinLensAperture(y) {
      return Math.abs(y - this.opticalAxisY) <= this.lens.height / 2;
    },
    computeImage() {
      const f = this.lens.focalLength;
      const d = this.d;

      // Too close to lens or too close to focal plane -> no sensible image
      if (Math.abs(d - f) < 2 || d < 10) {
        this.image = {
          x: Infinity,
          y: this.opticalAxisY,
          height: Infinity,
          real: false,
          upright: false,
          magnification: Infinity,
          exists: false,
          outOfView: true,
        };
        return;
      }

      const dPrime = (f * d) / (d - f);
      const xImage = this.lens.x + dPrime;
      const magnification = -dPrime / d;
      const hImage = this.obj.height * magnification;
      const yImage = this.opticalAxisY - hImage;

      // If image is way off-canvas we consider it not drawable
      const outOfView =
        xImage < -200 ||
        xImage > this.canvasWidth + 200 ||
        Math.abs(yImage - this.opticalAxisY) > this.canvasHeight;

      this.image = {
        x: xImage,
        y: yImage,
        height: Math.abs(hImage),
        real: d > f,
        upright: magnification > 0,
        magnification: Math.abs(magnification),
        exists: !outOfView,
        outOfView,
      };
    },

    // === Dragging ===
    onMouseDown(e) {
      const rect = this.$refs.canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;

      // Check object first (arrow head area)
      if (
        mx >= this.obj.x - 15 &&
        mx <= this.obj.x + 15 &&
        my >= this.opticalAxisY - this.obj.height - 15 &&
        my <= this.opticalAxisY + 15
      ) {
        this.dragTarget = "object";
        return;
      }

      // Check lens
      if (
        mx >= this.lens.x - this.lens.width - 10 &&
        mx <= this.lens.x + this.lens.width + 10 &&
        my >= this.opticalAxisY - this.lens.height / 2 - 10 &&
        my <= this.opticalAxisY + this.lens.height / 2 + 10
      ) {
        this.dragTarget = "lens";
        return;
      }
    },
    onMouseMove(e) {
      if (!this.dragTarget) return;
      const rect = this.$refs.canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left;

      if (this.dragTarget === "object") {
        let nx = mx;
        nx = Math.max(40, Math.min(nx, this.lens.x - 40));
        this.obj.x = nx;
      } else if (this.dragTarget === "lens") {
        let nx = mx;
        nx = Math.max(this.obj.x + 40, Math.min(nx, this.canvasWidth - 80));
        this.lens.x = nx;
      }
    },
    onMouseUp() {
      this.dragTarget = null;
    },

    // === Drawing ===
    drawArrow(ctx, fromX, fromY, toX, toY, color, dashed = false, lineWidth = 3) {
      ctx.beginPath();
      if (dashed) ctx.setLineDash([8, 6]);
      else ctx.setLineDash([]);
      ctx.moveTo(fromX, fromY);
      ctx.lineTo(toX, toY);
      ctx.strokeStyle = color;
      ctx.lineWidth = lineWidth;
      ctx.lineCap = "round";
      ctx.stroke();
      ctx.setLineDash([]);

      // Arrowhead
      const angle = Math.atan2(toY - fromY, toX - fromX);
      const headLen = 10;
      ctx.beginPath();
      ctx.moveTo(toX, toY);
      ctx.lineTo(
        toX - headLen * Math.cos(angle - Math.PI / 6),
        toY - headLen * Math.sin(angle - Math.PI / 6)
      );
      ctx.lineTo(
        toX - headLen * Math.cos(angle + Math.PI / 6),
        toY - headLen * Math.sin(angle + Math.PI / 6)
      );
      ctx.lineTo(toX, toY);
      ctx.fillStyle = color;
      ctx.fill();
    },

    drawOpticalAxis() {
      const ctx = this.ctx;
      ctx.beginPath();
      ctx.setLineDash([5, 5]);
      ctx.moveTo(0, this.opticalAxisY);
      ctx.lineTo(this.canvasWidth, this.opticalAxisY);
      ctx.strokeStyle = "rgba(255, 255, 255, 0.3)";
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.setLineDash([]);
    },

    drawLens() {
      const ctx = this.ctx;
      const lx = this.lens.x;
      const halfH = this.lens.height / 2;
      const w = this.lens.width;
      const bulge = w * 1.5; // степень выпуклости граней

      const topY = this.opticalAxisY - halfH;
      const bottomY = this.opticalAxisY + halfH;
      const leftX = lx - w / 2;
      const rightX = lx + w / 2;

      // === Основная форма линзы (симметричная биконвексная) ===
      ctx.beginPath();
      // левая грань — выпуклая наружу (влево)
      ctx.moveTo(leftX, topY);
      ctx.quadraticCurveTo(leftX - bulge, this.opticalAxisY, leftX, bottomY);
      // нижняя кромка
      ctx.lineTo(rightX, bottomY);
      // правая грань — выпуклая наружу (вправо)
      ctx.quadraticCurveTo(rightX + bulge, this.opticalAxisY, rightX, topY);
      ctx.closePath();

      // Градиентная заливка — имитация стекла
      const grad = ctx.createRadialGradient(
        lx, this.opticalAxisY - halfH * 0.25, w * 0.3,
        lx, this.opticalAxisY, halfH
      );
      grad.addColorStop(0, "rgba(200, 240, 255, 0.55)");
      grad.addColorStop(0.4, "rgba(120, 210, 255, 0.35)");
      grad.addColorStop(1, "rgba(80, 180, 240, 0.25)");
      ctx.fillStyle = grad;
      ctx.fill();

      // Обводка контура
      ctx.strokeStyle = "rgba(160, 230, 255, 0.9)";
      ctx.lineWidth = 2;
      ctx.stroke();

      // === Блики для эффекта стекла ===
      // Верхний блик
      ctx.beginPath();
      ctx.moveTo(leftX + w * 0.15, topY + halfH * 0.12);
      ctx.quadraticCurveTo(
        lx, topY + halfH * 0.08,
        rightX - w * 0.15, topY + halfH * 0.12
      );
      ctx.strokeStyle = "rgba(255, 255, 255, 0.6)";
      ctx.lineWidth = 2.5;
      ctx.stroke();

      // Нижний полублик
      ctx.beginPath();
      ctx.moveTo(leftX + w * 0.2, bottomY - halfH * 0.15);
      ctx.quadraticCurveTo(
        lx, bottomY - halfH * 0.1,
        rightX - w * 0.2, bottomY - halfH * 0.15
      );
      ctx.strokeStyle = "rgba(255, 255, 255, 0.25)";
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // === Оптический центр ===
      ctx.beginPath();
      ctx.arc(lx, this.opticalAxisY, 3, 0, Math.PI * 2);
      ctx.fillStyle = "#fff";
      ctx.fill();
    },

    drawFocalPoints() {
      const ctx = this.ctx;
      const f = this.lens.focalLength;
      const lx = this.lens.x;
      const y = this.opticalAxisY;

      const drawF = (x, label) => {
        ctx.beginPath();
        ctx.arc(x, y, 4, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(255, 200, 100, 0.9)";
        ctx.fill();
        ctx.font = "13px Arial";
        ctx.fillStyle = "rgba(255, 220, 150, 0.9)";
        ctx.textAlign = "center";
        ctx.fillText(label, x, y + 22);
      };

      drawF(lx - f, "F");
      drawF(lx + f, "F′");

      // 2F marks (small ticks)
      ctx.strokeStyle = "rgba(255, 200, 100, 0.5)";
      ctx.lineWidth = 1;
      [lx - 2 * f, lx + 2 * f].forEach((x) => {
        if (x > 0 && x < this.canvasWidth) {
          ctx.beginPath();
          ctx.moveTo(x, y - 6);
          ctx.lineTo(x, y + 6);
          ctx.stroke();
          ctx.fillStyle = "rgba(255, 200, 100, 0.6)";
          ctx.fillText("2F", x, y + 22);
        }
      });
    },

    drawObject() {
      const ctx = this.ctx;
      const x = this.obj.x;
      const top = this.opticalAxisY - this.obj.height;
      this.drawArrow(ctx, x, this.opticalAxisY, x, top, "#4ade80", false, 3);

      // Label
      ctx.font = "14px Arial";
      ctx.fillStyle = "#4ade80";
      ctx.textAlign = "center";
      ctx.fillText("Предмет", x, top - 12);
    },

    drawImage() {
      if (!this.image.exists) return;
      const ctx = this.ctx;
      const x = this.image.x;
      const y = this.image.y;
      const color = this.image.real ? "#f87171" : "#fbbf24";
      const dashed = !this.image.real;

      // Clip to avoid drawing too far off-canvas
      if (x < -500 || x > this.canvasWidth + 500) return;

      this.drawArrow(ctx, x, this.opticalAxisY, x, y, color, dashed, 3);

      ctx.font = "14px Arial";
      ctx.fillStyle = color;
      ctx.textAlign = "center";
      const labelY = y < this.opticalAxisY ? y - 12 : y + 22;
      ctx.fillText(this.image.real ? "Изображение" : "Мнимое изображение", x, labelY);
    },

    drawRays() {
      const ctx = this.ctx;
      const ox = this.obj.x;
      const oy = this.opticalAxisY - this.obj.height;
      const lx = this.lens.x;
      const axisY = this.opticalAxisY;
      const f = this.lens.focalLength;
      const d = this.d;
      const imageExists = this.image.exists;
      const ix = this.image.x;
      const iy = this.image.y;

      const drawRaySegment = (x1, y1, x2, y2, color, dashed = false, alpha = 1) => {
        ctx.beginPath();
        if (dashed) ctx.setLineDash([6, 5]);
        else ctx.setLineDash([]);
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.strokeStyle = color;
        ctx.globalAlpha = alpha;
        ctx.lineWidth = 2;
        ctx.shadowColor = color;
        ctx.shadowBlur = alpha < 1 ? 0 : 6;
        ctx.stroke();
        ctx.shadowBlur = 0;
        ctx.globalAlpha = 1;
        ctx.setLineDash([]);
      };

      // Ray 1: parallel to axis -> through F'
      const color1 = "#f87171"; // red
      const ray1hits = this.isWithinLensAperture(oy);
      // To lens plane
      drawRaySegment(ox, oy, lx, oy, color1, false, ray1hits ? 1 : 0.35);
      if (ray1hits) {
        if (imageExists && d > f) {
          drawRaySegment(lx, oy, ix, iy, color1, false);
          const beyondX = Math.min(this.canvasWidth, ix + 100);
          const beyondY = iy + ((iy - oy) / (ix - lx)) * (beyondX - ix);
          drawRaySegment(ix, iy, beyondX, beyondY, color1, false);
        } else if (imageExists && d < f) {
          const endX = this.canvasWidth;
          const slope1 = (axisY - oy) / f;
          const endY = oy + slope1 * (endX - lx);
          drawRaySegment(lx, oy, endX, endY, color1, false);
          if (ix > 0) drawRaySegment(lx, oy, ix, iy, color1, true);
        } else if (!imageExists) {
          const endX = this.canvasWidth;
          const slope1 = (axisY - oy) / f;
          const endY = oy + slope1 * (endX - lx);
          drawRaySegment(lx, oy, endX, endY, color1, false);
        }
      } else {
        // Missed lens: continue straight
        const endX = this.canvasWidth;
        drawRaySegment(lx, oy, endX, oy, color1, false, 0.25);
      }

      // Ray 2: through optical center (straight line) — always hits lens center
      const color2 = "#4ade80"; // green
      const slope2 = (axisY - oy) / (lx - ox);
      if (imageExists && d > f) {
        const beyondX = Math.min(this.canvasWidth, ix + 100);
        const beyondY = axisY + slope2 * (beyondX - lx);
        drawRaySegment(ox, oy, beyondX, beyondY, color2, false);
      } else if (imageExists && d < f) {
        const endX = this.canvasWidth;
        const endY = axisY + slope2 * (endX - lx);
        drawRaySegment(ox, oy, endX, endY, color2, false);
        if (ix > 0) drawRaySegment(lx, axisY, ix, iy, color2, true);
      } else if (!imageExists) {
        const endX = this.canvasWidth;
        const endY = axisY + slope2 * (endX - lx);
        drawRaySegment(ox, oy, endX, endY, color2, false);
      }

      // Ray 3: through F -> parallel after lens
      if (Math.abs(d - f) > 3) {
        const color3 = "#60a5fa"; // blue
        const yLens = imageExists ? iy : axisY + (oy - axisY) * d / (d - f);
        const ray3hits = this.isWithinLensAperture(yLens);
        drawRaySegment(ox, oy, lx, yLens, color3, false, ray3hits ? 1 : 0.35);
        if (ray3hits) {
          if (imageExists && d > f) {
            const beyondX = Math.min(this.canvasWidth, ix + 100);
            drawRaySegment(lx, yLens, beyondX, yLens, color3, false);
          } else if (imageExists && d < f) {
            const endX = this.canvasWidth;
            drawRaySegment(lx, yLens, endX, yLens, color3, false);
            if (ix > 0) drawRaySegment(lx, yLens, ix, yLens, color3, true);
          } else if (!imageExists) {
            const endX = this.canvasWidth;
            drawRaySegment(lx, yLens, endX, yLens, color3, false);
          }
        } else {
          // Missed lens: continue straight using incoming slope
          const slope3 = (yLens - oy) / (lx - ox);
          const endX = this.canvasWidth;
          const endY = yLens + slope3 * (endX - lx);
          drawRaySegment(lx, yLens, endX, endY, color3, false, 0.25);
        }
      }
    },

    animate() {
      if (!this.ctx || !this.$refs.canvas) return;
      const ctx = this.ctx;
      ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);

      this.drawOpticalAxis();
      this.drawFocalPoints();
      this.drawLens();
      this.drawObject();
      this.drawImage();
      this.drawRays();

      this.animationFrameId = requestAnimationFrame(this.animate);
    },
  },
};
</script>

<style scoped>
.optical-experiment {
  padding: 10px;
  font-family: Arial, sans-serif;
  background: #0f172a;
  color: #e2e8f0;
  border-radius: 8px;
}

#controls {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;
  margin-bottom: 10px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.control-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.control-group label {
  font-size: 14px;
  color: #94a3b8;
}

.control-group .value {
  font-weight: bold;
  color: #38bdf8;
  min-width: 30px;
  text-align: right;
}

.control-group .unit {
  font-size: 12px;
  color: #64748b;
}

input[type="range"] {
  width: 180px;
  cursor: pointer;
}

.btn-reset {
  padding: 6px 16px;
  background: #334155;
  color: #e2e8f0;
  border: 1px solid #475569;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.2s;
}

.btn-reset:hover {
  background: #475569;
}

.info-panel {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
  margin-bottom: 10px;
  padding: 10px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.info-item {
  font-size: 14px;
}

.info-label {
  color: #94a3b8;
  margin-right: 6px;
}

.info-value {
  font-weight: bold;
  color: #e2e8f0;
}

.nature-real {
  color: #4ade80;
}

.nature-virtual {
  color: #fbbf24;
}

#opticsCanvas {
  display: block;
  margin: 0 auto;
  border: 1px solid #334155;
  background: radial-gradient(circle, #1e293b 0%, #0f172a 100%);
  border-radius: 8px;
  cursor: crosshair;
}

.hint {
  text-align: center;
  margin-top: 10px;
  font-size: 13px;
  color: #64748b;
}
</style>
